import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ActionButton, IsSuperAdmin } from 'app/components';
import { useDialog, useSnakeBar, useUserGetEnvironmentsQuery, useUserUpdateEnvironmentsMutation } from 'app/hooks';
import { TranslationKeys } from 'app/translations';
import type { UseQueryRefetch, User } from 'app/types';

import { UserEnvironmentsListOwners } from './components';
import { UserEnvironmentsListConfirmDialog } from './components/UserEnvironmentsListConfirmDialog/UserEnvironmentsListConfirmDialog';
import type {
  UserEnvironmentsListOnChangeSelected,
  UserEnvironmentsListSelectedData,
} from './UserEnvironmentsList.types';

type UserEnvironmentsListProps = {
  user: User | undefined;
  refetchGetUserQuery: UseQueryRefetch;
};

export const UserEnvironmentsList = ({ user, refetchGetUserQuery }: UserEnvironmentsListProps) => {
  const isSuperAdmin = IsSuperAdmin();
  const { mutate: updateUserEnvironmentsMutation, isLoading: isUpdatingUser } = useUserUpdateEnvironmentsMutation();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const {
    dialogState: showConfirmDialog,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();
  const [defaultSelectedData, setDefaultSelectedData] = React.useState<UserEnvironmentsListSelectedData>();
  const [selectedData, setSelectedData] = React.useState<UserEnvironmentsListSelectedData>();
  const { data: owners = [], isLoading } = useUserGetEnvironmentsQuery({
    variables: { userId: user?.userId || '' },
    options: { enabled: isSuperAdmin && !!user?.userId },
  });

  React.useEffect(() => {
    const environmentOwnerIds = new Set(user?.environments?.map(environment => environment.ownerId) || []);
    const selected = {
      owners: owners.filter(owner => environmentOwnerIds.has(owner.ownerId)),
      areAllOwnersSelected: false,
    };
    setDefaultSelectedData(selected);
    setSelectedData(selected);
  }, [owners, user?.environments, user?.userId]);

  const hasChanged = JSON.stringify(defaultSelectedData?.owners || []) !== JSON.stringify(selectedData?.owners || []);

  const onChangeSelected = React.useCallback<UserEnvironmentsListOnChangeSelected>(newSelectedData => {
    setSelectedData(newSelectedData);
  }, []);

  const updateUserEnvironments = React.useCallback(() => {
    if (!user || !selectedData) return;

    const method = 'updateUserEnvironments';
    updateUserEnvironmentsMutation(
      {
        userId: user.userId,
        ownerIds: selectedData.owners
          .map(owner => owner.ownerId)
          .filter((ownerId): ownerId is string => typeof ownerId === 'string'),
      },
      {
        onSuccess: () => {
          closeConfirmDialog();
          refetchGetUserQuery();
          showSuccessSnakeBar({ method });
        },
        onError: (error: any) => showErrorSnakeBar({ method, message: error?.message }),
      },
    );
  }, [
    closeConfirmDialog,
    refetchGetUserQuery,
    selectedData,
    showErrorSnakeBar,
    showSuccessSnakeBar,
    updateUserEnvironmentsMutation,
    user,
  ]);

  const isDisabled = isLoading || isUpdatingUser;

  return isSuperAdmin ? (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={'h6'}>
          <FormattedMessage id={TranslationKeys.users_environmentsList_headerText} />
        </Typography>
      </Box>
      <UserEnvironmentsListOwners
        isLoading={isDisabled}
        onChangeSelected={onChangeSelected}
        selectedData={selectedData}
        owners={owners}
        openConfirmationDialog={() => undefined}
      ></UserEnvironmentsListOwners>
      {hasChanged && (
        <Box marginTop={3} marginBottom={3} display={'flex'} justifyContent={'end'} alignItems={'center'} gap={2}>
          <ActionButton
            variant={'outlined'}
            messageId={TranslationKeys.button_cancel}
            onClick={() => setSelectedData(defaultSelectedData)}
            disabled={isDisabled}
          />
          <ActionButton messageId={TranslationKeys.button_save} onClick={openConfirmDialog} disabled={isDisabled} />
        </Box>
      )}
      {showConfirmDialog && selectedData && (
        <UserEnvironmentsListConfirmDialog
          show={showConfirmDialog}
          isLoading={isDisabled}
          onClose={closeConfirmDialog}
          onConfirm={updateUserEnvironments}
          selectedData={selectedData}
          data={owners}
        />
      )}
    </>
  ) : null;
};
